import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { t } from '../i18n';
import { Button, type ButtonColor } from './button';

export interface DialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  confirmColor?: ButtonColor;
  loading?: boolean;
  confirmDisabled?: boolean;
  hideConfirm?: boolean;
  panelClassName?: string;
  footerAction?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}

export const Dialog = ({
  open,
  title,
  description,
  confirmText = t('confirm'),
  confirmColor = 'default',
  loading = false,
  confirmDisabled = false,
  hideConfirm = false,
  panelClassName = '',
  footerAction,
  onCancel,
  onConfirm,
}: DialogProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef({
    title,
    description,
    confirmText,
    confirmColor,
    loading,
    confirmDisabled,
    hideConfirm,
    panelClassName,
    footerAction,
  });
  if (open) {
    contentRef.current = {
      title,
      description,
      confirmText,
      confirmColor,
      loading,
      confirmDisabled,
      hideConfirm,
      panelClassName,
      footerAction,
    };
  }
  const content = contentRef.current;

  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frame = requestAnimationFrame(() => (hideConfirm ? cancelRef.current : confirmRef.current)?.focus());
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !loading) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hideConfirm, loading, onCancel, open]);

  return createPortal(
    <div
      className="dialog-backdrop fixed inset-0 z-100 grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
      data-state={open ? 'open' : 'closed'}
      inert={!open}
      onMouseDown={(event) => {
        if (open && event.target === event.currentTarget && !loading) {
          onCancel();
        }
      }}
    >
      <section
        className={`dialog-panel flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-(--line) bg-(--surface) p-6 shadow-2xl ${content.panelClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={content.description ? descriptionId : undefined}
      >
        <h2 className="m-0 shrink-0 text-lg font-bold" id={titleId}>
          {content.title}
        </h2>
        {content.description ? (
          <div
            className="dialog-description mt-2 min-h-0 overflow-y-auto text-sm leading-6 text-(--muted)"
            id={descriptionId}
          >
            {content.description}
          </div>
        ) : null}
        <footer className="mt-6 flex shrink-0 justify-end gap-2">
          {content.footerAction ? <div className="mr-auto">{content.footerAction}</div> : null}
          <Button ref={cancelRef} disabled={content.loading} onClick={onCancel}>
            {t('cancel')}
          </Button>
          {!content.hideConfirm ? (
            <Button
              ref={confirmRef}
              color={content.confirmColor}
              disabled={content.loading || content.confirmDisabled}
              onClick={onConfirm}
            >
              {content.confirmText}
            </Button>
          ) : null}
        </footer>
      </section>
    </div>,
    document.body,
  );
};
